import { create } from 'zustand';
import { useAuthStore } from './auth';
import { authedFetch } from '../api/authedFetch';
import type { SectionInfo } from '../types';

export interface AnswerPayload {
  section: string;
  question_index: number;
  content_id: number | null;
  sub_question_index: number | null;
  answer_data: Record<string, any>;
  time_spent_seconds: number;
  client_uuid: string;
}
export interface PendingAnswer {
  accountId: number;
  attemptId: number;
  payload: AnswerPayload;
  retries: number;
  maxRetries: number; // UI warning threshold; never a reason to discard an answer.
  nextRetryAt: number;
  lastError: string | null;
  serverRejection?: 'TIME_EXPIRED';
}
interface SessionData {
  accountId: number | null;
  attemptId: number | null;
  testType: string | null;
  sections: SectionInfo[];
  currentSection: string | null;
  currentQuestionIndex: number;
  timeRemaining: number | null;
  sectionDeadlineAt: number | null;
  answers: Record<string, Record<number, any>>;
  drafts: Record<string, Record<number, any>>;
  isFinished: boolean;
  questionType: string | null;
  drillConcept: string | null;
  drillCount: number | null;
  prefetchedQuestions: Record<string, any[]>;
  pendingAnswers: PendingAnswer[];
  hasTheta: boolean;
  mockMode: boolean;
  deadlineAt: string | null;
}
interface TestState extends SessionData {
  isPrefetchingQuestions: boolean;
  networkAvailable: boolean;
  startTest: (attemptId: number, testType: string, sections: SectionInfo[], currentSection: string, questionType?: string | null, drill?: { concept: string; count: number } | null, examMode?: { mockMode: boolean; deadlineAt: string | null }) => void;
  restoreAttempt: (attemptId: number, remote?: any) => boolean;
  setCurrentSection: (section: string) => void;
  setQuestionIndex: (index: number) => void;
  setTimeRemaining: (seconds: number) => void;
  saveAnswer: (section: string, questionIndex: number, answer: unknown) => void;
  saveDraft: (section: string, questionIndex: number, draft: unknown) => void;
  finishTest: (options?: { acceptedOnly: true }) => void;
  reset: () => void;
  setExamMode: (mockMode: boolean, deadlineAt: string | null) => void;
  prefetchQuestions: (attemptId: number) => Promise<boolean>;
  getNextQuestion: (section: string, index: number) => any | null;
  queueAnswer: (payload: Omit<AnswerPayload, 'client_uuid'>) => PendingAnswer;
  setPrefetchingState: (loading: boolean) => void;
  setNetworkAvailable: (available: boolean) => void;
  clearPendingAnswer: (clientUuid: string) => void;
  incrementPendingRetry: (clientUuid: string, error?: string, serverRejection?: 'TIME_EXPIRED') => void;
}
const emptySession = (): SessionData => ({
  accountId: null, attemptId: null, testType: null, sections: [], currentSection: null,
  currentQuestionIndex: 0, timeRemaining: null, sectionDeadlineAt: null, answers: {}, drafts: {},
  isFinished: false, questionType: null, drillConcept: null, drillCount: null,
  prefetchedQuestions: {}, pendingAnswers: [], hasTheta: false, mockMode: false, deadlineAt: null,
});
export const attemptStorageKey = (accountId: number, attemptId: number) => `edubot:attempt:v2:${accountId}:${attemptId}`;

function readSession(accountId: number, attemptId: number): SessionData | null {
  try {
    const saved = JSON.parse(localStorage.getItem(attemptStorageKey(accountId, attemptId)) || 'null');
    if (saved?.accountId !== accountId || saved?.attemptId !== attemptId || !Array.isArray(saved.sections) ||
        !Array.isArray(saved.pendingAnswers) || typeof saved.answers !== 'object') return null;
    if (saved.pendingAnswers.some((answer: PendingAnswer) => answer.accountId !== accountId ||
        answer.attemptId !== attemptId || typeof answer.payload?.client_uuid !== 'string')) return null;
    return { ...emptySession(), ...saved };
  } catch { return null; }
}

export const useTestStore = create<TestState>((set, get) => {
  // Persist before announcing that an answer/cursor is saved; storage errors remain visible.
  function commit(patch: Partial<SessionData>) {
    const next = { ...get(), ...patch };
    if (next.accountId && next.attemptId) {
      const snapshot = Object.fromEntries(Object.keys(emptySession()).map(key => [key, next[key as keyof SessionData]]));
      localStorage.setItem(attemptStorageKey(next.accountId, next.attemptId), JSON.stringify(snapshot));
    }
    set(patch);
  }
  return {
    ...emptySession(), isPrefetchingQuestions: false,
    networkAvailable: typeof navigator === 'undefined' || navigator.onLine,
    startTest(attemptId, testType, sections, currentSection, questionType, drill, examMode) {
      const accountId = useAuthStore.getState().user?.id;
      if (!accountId) throw new Error('Buka ulang EduBot dari Telegram untuk masuk.');
      const seconds = (sections.find(section => section.id === currentSection)?.duration_minutes || 0) * 60;
      commit({ ...emptySession(), accountId, attemptId, testType, sections, currentSection,
        questionType: questionType || null, drillConcept: drill?.concept || null, drillCount: drill?.count || null,
        mockMode: examMode?.mockMode || false, deadlineAt: examMode?.deadlineAt || null,
        sectionDeadlineAt: seconds > 0 ? Date.now() + seconds * 1000 : null });
    },
    restoreAttempt(attemptId, remote) {
      const accountId = useAuthStore.getState().user?.id;
      if (!accountId) return false;
      const local = get().accountId === accountId && get().attemptId === attemptId ? get() : readSession(accountId, attemptId);
      if (!remote) {
        if (!local) return false;
        set({ ...local, isPrefetchingQuestions: false });
        return true;
      }
      const metadata = typeof remote.metadata === 'object' && remote.metadata || {};
      const sections: SectionInfo[] = remote.sections || local?.sections || [];
      if (!sections.length || Number(remote.attempt_id ?? remote.id) !== attemptId) return false;
      const remoteSection = remote.current_section || sections[0].id;
      const localAhead = local && sections.findIndex(s => s.id === local.currentSection) > sections.findIndex(s => s.id === remoteSection);
      const currentSection = localAhead ? local.currentSection : remoteSection;
      const currentQuestionIndex = Math.max(local && currentSection === local.currentSection ? local.currentQuestionIndex : 0,
        currentSection === remoteSection ? Number(remote.current_question_index || 0) : 0);
      const remaining = Number.isFinite(remote.time_remaining_seconds) ? remote.time_remaining_seconds : null;
      const duration = (sections.find(s => s.id === currentSection)?.duration_minutes || 0) * 60;
      const remoteDeadline = Date.now() + (remaining ?? duration) * 1000;
      const answers = { ...local?.answers };
      for (const position of remote.answered_positions || []) {
        try {
          const answer = typeof position.answer_data === 'string' ? JSON.parse(position.answer_data) : position.answer_data;
          answers[position.section] = { ...answers[position.section], [position.question_index]: answer };
        } catch { /* A corrupt historical answer cannot replace an intact local copy. */ }
      }
      commit({ ...emptySession(), ...(local || {}), accountId, attemptId, sections,
        testType: remote.test_type || local?.testType, currentSection, currentQuestionIndex, answers,
        questionType: metadata.question_type || null, drillConcept: metadata.drill_concept || null,
        drillCount: metadata.drill_count || null, mockMode: Boolean(remote.mock_mode || metadata.mock_mode),
        deadlineAt: remote.deadline_at || metadata.deadline_at || null,
        sectionDeadlineAt: local && currentSection === local.currentSection && local.sectionDeadlineAt
          ? Math.min(local.sectionDeadlineAt, remoteDeadline) : remoteDeadline,
        isFinished: remote.status === 'completed',
      });
      return true;
    },
    setCurrentSection(section) {
      const seconds = (get().sections.find(s => s.id === section)?.duration_minutes || 0) * 60;
      commit({ currentSection: section, currentQuestionIndex: 0, timeRemaining: seconds,
        sectionDeadlineAt: seconds > 0 ? Date.now() + seconds * 1000 : null });
    },
    setQuestionIndex: index => commit({ currentQuestionIndex: index }),
    setTimeRemaining: seconds => commit({ timeRemaining: seconds }),
    saveAnswer(section, questionIndex, answer) {
      commit({ answers: { ...get().answers, [section]: { ...get().answers[section], [questionIndex]: answer } } });
    },
    saveDraft(section, questionIndex, draft) {
      commit({ drafts: { ...get().drafts, [section]: { ...get().drafts[section], [questionIndex]: draft } } });
    },
    finishTest(options) {
      const pending = get().pendingAnswers;
      if (pending.length && !(options?.acceptedOnly && pending.every(answer => answer.serverRejection === 'TIME_EXPIRED'))) {
        throw new Error('Masih ada jawaban yang belum terkirim.');
      }
      // Rejected late answers, drafts and their questions remain available on this device.
      commit({ isFinished: true, ...(pending.length ? {} : { prefetchedQuestions: {} }) });
    },
    reset: () => set({ ...emptySession(), isPrefetchingQuestions: false }),
    setExamMode: (mockMode, deadlineAt) => commit({ mockMode, deadlineAt }),
    async prefetchQuestions(attemptId) {
      const accountId = useAuthStore.getState().user?.id;
      if (get().attemptId !== attemptId || get().accountId !== accountId || get().isPrefetchingQuestions) return false;
      set({ isPrefetchingQuestions: true });
      try {
        const response = await authedFetch(`/api/tests/attempt/${attemptId}/questions-batch`);
        if (!response.ok) return false;
        const data = await response.json();
        if (get().attemptId !== attemptId || get().accountId !== accountId) return false;
        if (!data.questions_by_section || typeof data.questions_by_section !== 'object' ||
            !Object.values(data.questions_by_section).every(Array.isArray)) return false;
        commit({ prefetchedQuestions: data.questions_by_section, hasTheta: data.has_theta === true });
        return true;
      } catch { return false; }
      finally {
        if (get().attemptId === attemptId && get().accountId === accountId) set({ isPrefetchingQuestions: false });
      }
    },
    getNextQuestion: (section, index) => get().prefetchedQuestions[section]?.[index] || null,
    queueAnswer(payload) {
      const { accountId, attemptId } = get();
      if (!accountId || !attemptId || accountId !== useAuthStore.getState().user?.id) throw new Error('Sesi tes berubah. Buka ulang tes ini.');
      const existing = get().pendingAnswers.find(answer => answer.payload.section === payload.section && answer.payload.question_index === payload.question_index);
      if (existing) return existing;
      const answer: PendingAnswer = { accountId, attemptId,
        payload: { ...payload, client_uuid: crypto.randomUUID() }, retries: 0, maxRetries: 3, nextRetryAt: 0, lastError: null };
      commit({ pendingAnswers: [...get().pendingAnswers, answer],
        answers: { ...get().answers, [payload.section]: { ...get().answers[payload.section], [payload.question_index]: payload.answer_data } } });
      return answer;
    },
    setPrefetchingState: loading => set({ isPrefetchingQuestions: loading }),
    setNetworkAvailable: available => set({ networkAvailable: available }),
    clearPendingAnswer: clientUuid => commit({ pendingAnswers: get().pendingAnswers.filter(answer => answer.payload.client_uuid !== clientUuid) }),
    incrementPendingRetry(clientUuid, error = 'Jawaban belum terkirim', serverRejection) {
      commit({ pendingAnswers: get().pendingAnswers.map(answer => answer.payload.client_uuid === clientUuid
        ? { ...answer, retries: answer.retries + 1, nextRetryAt: Date.now() + Math.min(300_000, 5000 * 2 ** Math.min(answer.retries, 6)), lastError: error,
            ...(serverRejection ? { serverRejection } : {}) }
        : answer) });
    },
  };
});

useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) useTestStore.getState().reset();
});
